import React, { useState, useEffect, useCallback } from 'react';
import { Card, Row, Col, Alert, Spin, Space, Tag, Typography, Select } from 'antd';
import { useSelector } from 'react-redux';
import {
  fetchDashboardFilterOptions,
  fetchDashboardAnalytics,
  fetchAllSessions
} from '../../../../Service/Dashboard_API/dashboardApi';
import { VideoCameraOutlined, BookOutlined, DesktopOutlined, DashboardOutlined, FileTextOutlined } from '@ant-design/icons';
import {
  ClusterInstitutionAnalyticsCard
} from './AnalyticsCharts';
import ClusterLiveAttendanceReportCard from './ClusterLiveAttendanceReportCard';

const { Text, Title } = Typography;
const { Option } = Select;

const StudioDashboard = ({ onMenuSelect }) => {
  const token = useSelector((state) => state.auth?.token);

  // Filter state
  const [filters, setFilters] = useState({
    academic_year_id: 'all',
    institution_id: 'all',
    instructor_id: 'all',
    menu_key: 'all'
  });
  const [filterOptions, setFilterOptions] = useState({});
  const [analytics, setAnalytics] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const [liveSessions, setLiveSessions] = useState([]);
  const [isFilterLoaded, setIsFilterLoaded] = useState(false);

  // Fetch filter options on mount & default to current active Academic Year
  useEffect(() => {
    const loadFilterOptions = async () => {
      try {
        const res = await fetchDashboardFilterOptions();
        if (res.success) {
          setFilterOptions(res);
          if (res.academicYears && res.academicYears.length > 0) {
            const activeAy = res.academicYears.find(
              y => y.status?.toLowerCase() === 'active' || y.status === 'Active' || y.status === 'ACTIVE' || y.status === 1 || y.status === '1'
            ) || res.academicYears.find(y => {
              if (!y.start_date || !y.end_date) return false;
              const today = new Date();
              const start = new Date(y.start_date);
              const end = new Date(y.end_date);
              return !isNaN(start.getTime()) && !isNaN(end.getTime()) && today >= start && today <= end;
            }) || res.academicYears[0];

            if (activeAy) {
              const defaultAyId = String(activeAy.academic_year_id);
              setFilters((prev) => ({ ...prev, academic_year_id: defaultAyId }));
            }
          }
        }
      } catch (err) {
        console.error('Failed to load filter options:', err);
      } finally {
        setIsFilterLoaded(true);
      }
    };
    loadFilterOptions();
  }, []);

  const loadAnalytics = useCallback(async (isBackground = false) => {
    try {
      if (!isBackground) {
        setLoading(true);
        setError(null);
      }
      const res = await fetchDashboardAnalytics(filters);
      if (res && res.success) {
        setAnalytics(res);
      } else if (!isBackground) {
        setError(res?.message || 'Failed to load studio analytics');
      }
    } catch (err) {
      console.error('Studio analytics load error:', err);
      if (!isBackground) {
        setError(err.message || 'Error connecting to analytics');
      }
    } finally {
      if (!isBackground) {
        setLoading(false);
      }
    }
  }, [filters]);

  useEffect(() => {
    if (!isFilterLoaded) return;
    loadAnalytics();
    const analyticsInterval = setInterval(() => {
      loadAnalytics(true);
    }, 15000);
    return () => clearInterval(analyticsInterval);
  }, [isFilterLoaded, loadAnalytics]);

  useEffect(() => {
    const fetchLive = async () => {
      try {
        const data = await fetchAllSessions();
        const sessionList = Array.isArray(data)
          ? data
          : (data && data.success && Array.isArray(data.sessions) ? data.sessions : (data && Array.isArray(data.sessions) ? data.sessions : []));
        setLiveSessions(sessionList);
      } catch (err) {
        console.error('Failed to fetch live sessions:', err);
      }
    };
    fetchLive();
    const interval = setInterval(fetchLive, 10000);
    return () => clearInterval(interval);
  }, []);

  
  
  const summary = analytics?.summary || {};
  const liveCount = liveSessions.filter((s) => s.status === 'live').length;
  const scheduledCount = liveSessions.filter((s) => s.status === 'scheduled').length;

  const cardItems = [
    {
      title: 'Live Studio Rooms',
      value: liveCount,
      icon: <VideoCameraOutlined style={{ fontSize: 24, color: liveCount > 0 ? '#ef4444' : '#60a5fa' }} />,
      bgColor: liveCount > 0 
        ? 'linear-gradient(135deg, rgba(239, 68, 68, 0.12) 0%, rgba(220, 38, 38, 0.04) 100%)'
        : 'linear-gradient(135deg, rgba(96, 165, 250, 0.12) 0%, rgba(59, 130, 246, 0.04) 100%)',
      borderColor: liveCount > 0 ? 'rgba(239, 68, 68, 0.2)' : 'rgba(96, 165, 250, 0.2)',
      tag: liveCount > 0 ? `🔴 Active (${liveCount} live)` : scheduledCount > 0 ? `Offline (${scheduledCount} scheduled)` : 'No Live Sessions',
      tagColor: liveCount > 0 ? 'red' : scheduledCount > 0 ? 'gold' : 'default',
      targetMenu: 'live-sessions'
    },
    {
      title: 'Active Courses',
      value: summary.totalCourses || 0,
      icon: <BookOutlined style={{ fontSize: 24, color: '#10b981' }} />,
      bgColor: 'linear-gradient(135deg, rgba(16, 185, 129, 0.12) 0%, rgba(4, 120, 87, 0.04) 100%)',
      borderColor: 'rgba(16, 185, 129, 0.2)',
      tag: 'Assigned courses',
      tagColor: 'green',
      targetMenu: 'courses'
    },
    {
      title: 'Study Modules',
      value: summary.totalModules || 0,
      icon: <DesktopOutlined style={{ fontSize: 24, color: '#f59e0b' }} />,
      bgColor: 'linear-gradient(135deg, rgba(245, 158, 11, 0.12) 0%, rgba(217, 119, 6, 0.04) 100%)',
      borderColor: 'rgba(245, 158, 11, 0.2)',
      tag: 'Syllabus modules',
      tagColor: 'orange',
      targetMenu: 'courses'
    },
    {
      title: 'Video Lectures',
      value: summary.totalVideoIndex || 0,
      icon: <DashboardOutlined style={{ fontSize: 24, color: '#3b82f6' }} />,
      bgColor: 'linear-gradient(135deg, rgba(59, 130, 246, 0.12) 0%, rgba(29, 78, 216, 0.04) 100%)',
      borderColor: 'rgba(59, 130, 246, 0.2)',
      tag: 'Indexed lectures',
      tagColor: 'blue',
      targetMenu: 'courses'
    }
  ];

  return (
    <div style={{ padding: '4px 0' }}>
      {error && <Alert message="Studio Error" description={error} type="error" showIcon style={{ marginBottom: 20 }} />}

      {loading && !analytics ? (
        <div style={{ textAlign: 'center', padding: '60px 0' }}>
          <Spin size="large" tip="Loading Studio Analytics..." />
        </div>
      ) : (
        <>
          <Card
            title={
              <Text strong style={{ fontSize: 16, color: 'var(--text-color, #374151)' }}>
                Executive Metrics Overview
              </Text>
            }
            extra={
              <Space size="small">
                <Text strong style={{ fontSize: 13 }}>Academic Year:</Text>
                <Select showSearch optionFilterProp="children"
                  size="small"
                  value={filters.academic_year_id}
                  onChange={(val) => setFilters(prev => ({ ...prev, academic_year_id: val }))}
                  style={{ width: 140 }}
                  popupMatchSelectWidth={false}
                >
                  <Option value="all">All Academic Years</Option>
                  {(filterOptions?.academicYears || []).map((ay) => (
                    <Option key={ay.academic_year_id} value={String(ay.academic_year_id)}>
                      {ay.academic_year}
                    </Option>
                  ))}
                </Select>
              </Space>
            }
            style={{
              borderRadius: 16,
              marginBottom: 24,
              marginTop: 24,
              background: 'var(--panel-color, #ffffff)',
              borderColor: 'var(--border-color, rgba(0,0,0,0.08))'
            }}
          >
            <Row gutter={[16, 16]}>
              {cardItems.map((item, idx) => (
                <Col xs={24} sm={12} lg={6} key={idx} style={{ flexGrow: 1 }}>
                  <Card
                    hoverable
                    onClick={() => {
                      if (item.onClick) {
                        item.onClick();
                      } else if (onMenuSelect) {
                        onMenuSelect(item.targetMenu);
                      }
                    }}
                    style={{
                      borderRadius: 12,
                      background: item.bgColor,
                      borderColor: item.borderColor,
                      boxShadow: '0 4px 15px rgba(0,0,0,0.03)'
                    }}
                    bodyStyle={{ padding: '16px 18px' }}
                  >
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                      <div>
                        <Text type="secondary" style={{ fontSize: 12, fontWeight: 600 }}>
                          {item.title}
                        </Text>
                        <Title level={3} style={{ margin: '4px 0', fontWeight: 800, color: 'var(--text-color, #1f2937)' }}>
                          {item.value}
                        </Title>
                      </div>
                      <div
                        style={{
                          padding: 10,
                          borderRadius: 12,
                          backgroundColor: 'var(--panel-color, rgba(255, 255, 255, 0.85))',
                          border: '1px solid var(--border-color)',
                          backdropFilter: 'blur(4px)'
                        }}
                      >
                        {item.icon}
                      </div>
                    </div>
                    <div style={{ marginTop: 8 }}>
                      <Tag color={item.tagColor} style={{ borderRadius: 10, fontWeight: 600 }}>
                        {item.tag}
                      </Tag>
                    </div>
                  </Card>
                </Col>
              ))}
            </Row>
          </Card>



          <div style={{ marginBottom: 24 }}>
            <ClusterLiveAttendanceReportCard
              academicYears={filterOptions?.academicYears}
              clusters={filterOptions?.clusters}
              defaultAcademicYearId={filters.academic_year_id !== 'all' ? filters.academic_year_id : null}
            />
          </div>

          <div style={{ marginBottom: 24 }}>
            <ClusterInstitutionAnalyticsCard
              InstitutionsData={analytics?.clusterInstitutionStats}
              academicYears={filterOptions?.academicYears}
              clusters={filterOptions?.clusters}
            />
          </div>
        </>
      )}
    </div>
  );
};

export default StudioDashboard;
